import { useEffect, useId, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthResponse } from '@clinikchat/shared';
import api from '../../lib/api';
import { PRIVACY_POLICY_VERSION, PRIVACY_POLICY_DISPLAY_DATE } from '../../lib/privacy-version';
import { useAuthStore } from '../../stores/authStore';

type ConsentAwareUser = AuthResponse['user'] & {
  acceptedPrivacyPolicyVersion?: string | null;
  [key: string]: unknown;
};

interface ReconsentState {
  showModal: boolean;
  isSubmitting: boolean;
  error: string | null;
  handleConsent: () => Promise<void>;
}

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function apiErrorCode(err: unknown): string | undefined {
  return (err as { response?: { data?: { error?: { code?: string } } } } | null)?.response?.data
    ?.error?.code;
}

function apiErrorMessage(err: unknown): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } } | null)?.response?.data
      ?.error?.message || 'Failed to save consent. Please try again.'
  );
}

/**
 * Detect whether the signed-in user must accept the active privacy policy.
 *
 * The normal profile endpoint is intentionally blocked by the API re-consent gate, so the
 * hook recognises `CONSENT_REQUIRED` as well as a stale version already present in either
 * the auth store or the shared `['me']` query. On acceptance, the narrower consent response
 * is merged into both stores without discarding profile, workspace, or onboarding fields.
 *
 * @returns Modal visibility, submission state, an error message, and the consent action.
 */
export function useReconsent(): ReconsentState {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const accessToken = useAuthStore((state) => state.accessToken);
  const storedUser = useAuthStore((state) => state.user) as ConsentAwareUser | null;
  const setUser = useAuthStore((state) => state.setUser);

  const { data: user, error: userError } = useQuery<ConsentAwareUser>({
    queryKey: ['me'],
    queryFn: async () => {
      const response = await api.get('/api/v1/users/me');
      return response.data.data;
    },
    enabled: !!accessToken,
    retry: false,
  });

  // GET /users/me is deliberately consent-gated. Detect its 403 response as
  // the authoritative signal, while also using an already-hydrated user when
  // available (for example after silent refresh or a successful consent POST).
  const acceptedVersion =
    user?.acceptedPrivacyPolicyVersion ?? storedUser?.acceptedPrivacyPolicyVersion;
  const showModal =
    apiErrorCode(userError) === 'CONSENT_REQUIRED' ||
    (acceptedVersion !== undefined && acceptedVersion !== PRIVACY_POLICY_VERSION);

  const handleConsent = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await api.post('/api/v1/users/consent', {
        version: PRIVACY_POLICY_VERSION,
      });
      const updatedUser = response.data.data as ConsentAwareUser;
      const cachedUser = queryClient.getQueryData<ConsentAwareUser>(['me']);
      const mergedUser = {
        ...storedUser,
        ...cachedUser,
        ...updatedUser,
      } as ConsentAwareUser;
      queryClient.setQueryData(['me'], mergedUser);
      setUser(mergedUser);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return { showModal, isSubmitting, error, handleConsent };
}

export function ReconsentModal() {
  const { showModal, isSubmitting, error, handleConsent } = useReconsent();
  const dialogRef = useRef<HTMLDivElement>(null);
  const acceptButtonRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    if (!showModal) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    const id = window.setTimeout(() => acceptButtonRef.current?.focus(), 0);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;

      const focusable = Array.from(root.querySelectorAll<HTMLElement>(focusableSelector));
      if (focusable.length === 0) {
        event.preventDefault();
        root.focus();
        return;
      }

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (event.shiftKey && (active === first || !root.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !root.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('keydown', handleKeyDown);
      try {
        previouslyFocusedRef.current?.focus?.();
      } catch {
        // The previously focused element may have been removed while consent was open.
      }
    };
  }, [showModal]);

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center bg-black/50 px-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="w-full max-w-md rounded-2xl border border-warm-border bg-white p-6 shadow-warm-lg outline-none dark:border-warm-dark-border dark:bg-warm-dark-surface"
      >
        <h2 id={titleId} className="text-lg font-semibold text-warm-gray-900 dark:text-warm-white">
          Privacy Policy Update
        </h2>
        <p className="mt-3 text-sm text-warm-gray-700 dark:text-warm-gray-300">
          The privacy policy was updated on <strong>{PRIVACY_POLICY_DISPLAY_DATE}</strong>. Please
          review and accept the updated policy to continue using ClinikChat.
        </p>

        <div className="my-4 max-h-64 overflow-y-auto rounded border border-warm-border-light bg-warm-cream p-3 text-xs text-warm-gray-600 dark:border-warm-dark-border dark:bg-warm-dark-bg dark:text-warm-gray-400">
          <p className="mb-2">
            <strong>What changed:</strong> Review the full policy at{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary">
              /privacy
            </a>
          </p>
          <p className="text-xs text-warm-gray-500 dark:text-warm-gray-400">
            Version {PRIVACY_POLICY_VERSION}
          </p>
        </div>

        {error && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="flex gap-3">
          <button
            ref={acceptButtonRef}
            onClick={handleConsent}
            disabled={isSubmitting}
            className="flex-1 rounded-lg bg-primary px-4 py-2 font-medium text-white hover:bg-primary-hover disabled:opacity-50"
          >
            {isSubmitting ? 'Accepting...' : 'Accept & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
